/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../index';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { LanguageService, NavigationService, NotifyService } from '../../../services';
import { NotificationEnum } from 'src/app/core/enums';
import { NotificationModel } from '../../../models';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-notifications-banner',
    templateUrl: './notificationBanner.component.html',
    styleUrls: ['./notificationBanner.component.scss'],
    encapsulation: ViewEncapsulation.None // Added to allow innerHTML to be style matched
})

export class NotificationBannerComponent extends BaseComponent implements OnInit {
    /**
     * Incoming notification object
     */
    @Input() public notification: NotificationModel;

    /**
     * Force hide buttons on notification
     */
    @Input() public hideButtons: boolean = false;

    /**
     * Notification hide timer time
     */
    public notificationTimeout: number = 5000;

    /**
     * Notifications hide timer reference for removal
     */
    public timer: any;

    /**
     * Toggle is open.
     */
    public toggleIsOpen: boolean = false;

    /**
     * Reference to Notification enum.
     * @protected
     */
    protected readonly NotificationEnum = NotificationEnum;

    constructor(
        private languageService: LanguageService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();

        this.notifyService.removeFromToasterEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notification: NotificationModel): void => {
                    // Clear the timer if the notification has already been removed else where.
                    if (notification.id === this.notification.id && notification.level === this.notification.level) {
                        clearTimeout(this.timer);
                    }
                }
            });
    }

    ngOnInit(): void {
        if (this.notification.type === 'general' && this.notification.level !== 1) {
            this.setRemoveTimer();
        }
    }

    /**
     * Image path for notification level.
     * @param level
     */
    public getImagePathByLevel(level: number): string {
        let imageName = 'solid-exclamation-circle';
        switch (level) {
            case NotificationEnum.ERROR:
            case NotificationEnum.WARNING:
                imageName = 'solid-exclamation-triangle';
                break;
            case NotificationEnum.SUCCESS:
                imageName = 'solid-checked-circle';
                break;
            case NotificationEnum.INFO:
                imageName = 'solid-exclamation-circle';
                break;
            case NotificationEnum.DEBUG:
                imageName = 'outline-tools';
                break;
        }

        return `assets/shared/icons/notification-${imageName}.svg`;
    }

    /**
     * Get message length.
     * @param notification
     */
    public getMessageLength(notification: NotificationModel): number {
        if (notification.messageTranslate) {
            return this.languageService.translate(notification.message).length;
        } else {
            return notification.message.length;
        }
    }

    /**
     * Timer to automatically remove the notification.
     */
    public setRemoveTimer(): void {
        clearTimeout(this.timer);

        this.timer = setTimeout(() => {
            this.notifyService.removeNotificationById(this.notification.id);
        }, this.notificationTimeout);
    }

    /**
     * Redirect to another page.
     * @param url
     * @param urlTarget
     * @param pageReload
     * @param urlCloseOnClick
     */
    public navigate(url: string, urlTarget: string, pageReload: boolean, urlCloseOnClick: boolean): void {
        let cleanUrl = url;
        let queryParams = null;
        if (pageReload) {
            location.reload();
        }

        if (!url) {
            return;
        }

        if (urlCloseOnClick) {
            this.notifyService.removeNotificationById(this.notification.id);
        }

        if (url.includes('?sFilter=')) {
            const query = url.split('?sFilter=')[1];
            queryParams = { sFilter: query };
            cleanUrl = url.replace('?sFilter=' + query, '');
        }

        if (urlTarget === '_self') {
            const formattedUrl = this.environment.module === this.modulesEnum.LOADBALANCER
                ? this.navigationService.getEnduranceUrl('/' + cleanUrl) : cleanUrl;

            this.router.navigate([formattedUrl], { queryParams, relativeTo: this.route.firstChild }).then().catch();
        } else {
            window.open(url, '_blank');
        }
    }

    /**
     * Gets notifications class name from the level.
     * @param level
     */
    public getLevelClassName(level: string | number): string {
        return ({
            5: 'debug',
            4: 'information',
            3: 'success',
            2: 'warning',
            1: 'error'
        })[level];
    }

    /**
     * Helper function to remove notification.
     */
    public removeNotification(): void {
        this.notifyService.removeNotificationById(this.notification.id);
    }

    /**
     * Helper function to hide notification.
     */
    public hideNotification(): void {
        this.notifyService.hideNotificationById(this.notification.id);
    }

    /**
     * Toggle expands and collapses the notification text.
     */
    public toggleNotificationText(): void {
        this.toggleIsOpen = !this.toggleIsOpen;
    }
}
